/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import { IGatsbyImageData, getSrc } from 'gatsby-plugin-image';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';

interface SEOProps {
  description: string;
  lang?: string;
  meta?: Array<any>;
  title: string;
  image?: IGatsbyImageData;
  isProductSvg?: boolean;
}

function SEO({ description = ``, lang = 'en', meta = [], title, image, isProductSvg }: SEOProps) {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
          siteUrl
          image
        }
      }
    }
  `);

  const metaImage = (image as any)?.childImageSharp
    ? site.siteMetadata.siteUrl + getSrc(image)
    : isProductSvg
    ? site.siteMetadata.siteUrl + (image as any)?.publicURL
    : site.siteMetadata.image;

  const location = useLocation();

  useEffect(() => {
    async function pushDataLayerObject() {
      const profile = (window as any).auth ? await (window as any).auth.getProfile() : {};
      const id = profile['http://arduino.cc/id'];
      (window as any).dataLayer.push({
        event: 'virtualPageView',
        page: {
          type: 'content',
          title: title,
          path: window.location.pathname,
          environment: process.env.GATSBY_HF_ENV,
          region: 'global',
          brand: 'arduino',
          language: 'EN',
        },
        ...(profile['http://arduino.cc/is_minor'] && { userkind: 'minor' }),
        user: {
          id,
        },
      });
    }
    pushDataLayerObject();
  }, [title]);

  const ldJson = {
    '@context': 'https://schema.org',
    '@type': 'content',
    headline: title,
    image: [site.siteMetadata.siteUrl + (image as any)?.publicURL],
  };

  return (
    <Helmet>
      <title>{title + ' | ' + site.siteMetadata.title}</title>

      {isProductSvg && <script type="application/ld+json">{JSON.stringify(ldJson)}</script>}

      <meta name="description" content={description || site.siteMetadata.description} />

      {/* OpenGraph */}
      <meta name="og:title" content={title + ' | ' + site.siteMetadata.title} />
      <meta name="og:description" content={description || site.siteMetadata.description} />
      <meta name="og:image" content={metaImage} />
      <meta name="og:url" content={site.siteMetadata.siteUrl + location.pathname} />
      <meta name="og:type" content="website" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title + ' | ' + site.siteMetadata.title} />
      <meta name="twitter:description" content={description || site.siteMetadata.description} />
      <meta name="twitter:creator" content={site.siteMetadata.author} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:url" content={site.siteMetadata.siteUrl + location.pathname} />
      <meta name="twitter:domain" content={site.siteMetadata.domain} />
    </Helmet>
  );
}

export default SEO;
